import React from 'react';
import { AddSquareIcon, CloseIcon, DownloadIcon, FilterIcon, SearchIcon } from '../../../assets/icons';
import InventoryFilter from '../InventoryFilter';
import { Filter } from '../../../@types/inventory';
import { DropDown, SearchInput } from '../../common';
import { DropdownComponent } from '../../core/Forms';
import Chip from '../../common/Chip';
import { FILTER_LIST } from '../../../constants';

type MemoizedSelectedFilter = (selectedFilter: Filter) => Filter;

interface InventoryMobileHeadProps {
  selectedFilter: Filter | undefined;
  handleStockStatusChange: (newStockStatus: string) => void;
  handleVariantStatusChange: (newStockStatus: string) => void;
  onSubmit: any;
  OnSearch?: (text: string) => void;
  handleFilter?: (filter: Filter) => void;
  handleSort?: (text: string) => void;
  handleSearchSelect?: (item: any) => void;
  isSearching?: boolean;
  searchResult: any[];
  searchDisplayKey: string;
  searchPlaceHolder: string;
}

const InventoryMobileHead: React.FC<InventoryMobileHeadProps> = ({
  selectedFilter,
  handleStockStatusChange,
  handleVariantStatusChange,
  onSubmit,
  OnSearch,
  handleFilter,
  handleSort,
  handleSearchSelect,
  searchResult,
  searchDisplayKey,
  searchPlaceHolder,

}) => {
  const [showFilter, setShowFilter] = React.useState(false);
    const [text, setText] = React.useState('');
    const [selectedValue, setSelectedValue] = React.useState(' Recent');
    const dropDownArray = FILTER_LIST;
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(true);
    
  const handleFilterIconClick = () => {
    setShowFilter(true);
  };

  const handleCloseModal = () => {
    setShowFilter(false);
  };

  const handleSearch = async (text: string) => {
    if (!text || text.length < 2) return;

    try {
      setIsSearching(true);
      await OnSearch?.(text);
    } catch (error) {
      console.error('Error occurred during search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleDropDownChange = (value: string) => {
    handleSort ? handleSort(selectedValue) : null;
  };

  const handleChipClose = (chipKey: keyof Filter) => {
    if (selectedFilter) {
      let newFilter = { ...selectedFilter };
      if (chipKey in newFilter) {
        if (typeof newFilter[chipKey] === 'string') {
          (newFilter[chipKey] as string) = '';
        }
      }
      handleFilter ? handleFilter(newFilter as Filter) : undefined;
      console.log('Filtering with filter:', handleFilter);
    }
  };

  const renderFilterChip = () => {
    const chips = [];
    const filterLabels = {
      stockStatus: 'stockStatus',
      variantStatus: 'variantStatus',
    };

    if (selectedFilter) {
      for (const key in filterLabels) {
        const filterKey = key as keyof Filter;
        if (filterKey in selectedFilter && selectedFilter[filterKey] !== null && selectedFilter[filterKey] !== '') {
          const label = selectedFilter[filterKey];
          chips.push(<Chip key={filterKey} label={label} clearAction={() => handleChipClose(filterKey)} />);
        }
      }
    }

    return chips;
  };

  const memoizedSelectedFilter = React.useMemo(
    () => selectedFilter,
    [
      selectedFilter && 'stockStatus' in selectedFilter ? selectedFilter.stockStatus : null,
      selectedFilter && 'variantStatus' in selectedFilter ? selectedFilter.variantStatus : null,
    ],
  );
  const Dropdown = (
    <DropDown
      onChange={handleFilterIconClick}
      show={showFilter}
      component={
        <div className="flex ">
          <span>Filter</span> <FilterIcon color="#044E97" />{' '}
        </div>
      }
    >
      <InventoryFilter
        variantStatus={
          memoizedSelectedFilter && 'variantStatus' in memoizedSelectedFilter
            ? memoizedSelectedFilter.variantStatus
            : undefined
        }
        setStockStatus={handleStockStatusChange}
        setVariantStatus={handleVariantStatusChange}
        stockStatus={
          memoizedSelectedFilter && 'stockStatus' in memoizedSelectedFilter
            ? memoizedSelectedFilter.stockStatus
            : undefined
        }
        onSubmit={onSubmit}
        onClose={handleCloseModal}
        showFilter
      />
    </DropDown>
  );

  return (
    <div>
      <div className="flex justify-between text-[#044E97] font-medium md:hidden mb-4">
        <div className="flex items-center border border-r-gray-200 bg-gray-50 p-2 gap-2 h-[38px] text-[#044E97] rounded-[4px] justify-between w-fit">
          <a href="/inventories/create" className="text-[14px]">
            Add Inventory
          </a>
          <div>
            <AddSquareIcon />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center border border-r-gray-200 bg-gray-100 p-2 gap-2 h-[38px] rounded-[4px] justify-between w-fit">
            <p className="text-[14px]">Import</p>
            <div>
              <DownloadIcon />
            </div>
          </div>
          <div className="flex items-center border border-r-gray-200 bg-gray-50 p-2 gap-2 h-[38px] rounded-[4px] justify-between w-fit">
            <div>{Dropdown}</div>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center md:justify-end md:gap-4 lg:gap-36 xl:gap-56 lg:mt-0">
        <div className="relative md:hidden lg:block">
          <SearchInput
            placeholder="Search for Inventory"
            displayKey={searchDisplayKey}
            isLoading={isSearching}
            value={text}
            onSelect={handleSearchSelect}
            handleChangeText={handleSearch}
            clearIcon={CloseIcon}
            data={searchResult}
            emptyMessage={'This Item is not found'}
            dropdownStyle={{
              maxHeight: '800px',
              overflowY: 'auto',
            }}
            classNames="w-[202px] block p-4 pl-10 md:w-[292px] xl:w-[300px] lg:w-[200px] lg:mt-[0em] lg:ml-[0em] !xl:ml-[0em] border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] h-[39.77px] rounded p-2 mt-[5px] focus:outline-none focus:shadow-outline appearance-none lg:focus:ring-0 leading-normal"
          />
          <div className="absolute inset-y-0 left-[-1px] flex items-center pl-3 pointer-events-none">
            <SearchIcon />
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center">
            <DropdownComponent
              options={dropDownArray}
              value={selectedValue}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              isSubmitted={isSubmitted}
              onChange={handleDropDownChange}
              dropdownClassName="w-[140px] md:max-w-[200px] md:mt[0 lg:mt-0  xl:w-full md:mr-[0em] lg:w-[120px] xl:w-full lg:ml-[-7em] xl:ml-[-10em] lg:mt-0 xl:w-[300px] bg-white rounded xl:rounded xl:focus:ring-0 xl:pointer-events "
              iconClassName="xl:mt-[3px] mr-[1em] mt-[0em] md:mt-[0.3em] md:mr-[1.2em] md:mt-[0.5em] xl:mr-[4em]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { InventoryMobileHead };
